import { alpha, keyframes, styled } from "@mui/material/styles";

/**
 * The app's one "this just changed" mark: a tint that fades in, holds, and fades out.
 *
 * Shared so a change looks the same wherever it lands — a proposal field another party changed, a
 * permission row the reader just changed. It is a sibling overlay rather than a style on the thing
 * that changed: `position: absolute` keeps it out of the layout, and `pointer-events: none` plus
 * `aria-hidden` keep it out of the pointer's way, the focus order and the accessibility tree. Place
 * it inside a `position: relative` container.
 */

/** How long a changed thing stays marked. */
export const CHANGE_HIGHLIGHT_DURATION_MS = 3000;

const changeFade = keyframes`
  0% { opacity: 0; }
  10% { opacity: 1; }
  65% { opacity: 1; }
  100% { opacity: 0; }
`;

export const ChangeHighlightTint = styled("span", { shouldForwardProp: (prop) => prop !== "animate" })<{
  animate: boolean;
}>(({ theme, animate }) => ({
  backgroundColor: alpha(theme.palette.info.main, 0.22),
  borderRadius: theme.shape.borderRadius,
  bottom: 0,
  left: 0,
  pointerEvents: "none",
  position: "absolute",
  right: 0,
  top: 0,
  zIndex: 0,
  ...(animate ? { animation: `${changeFade} ${CHANGE_HIGHLIGHT_DURATION_MS}ms ease-out forwards` } : {}),
}));

/**
 * Whether this viewer has asked for less motion. Read when a highlight mounts rather than subscribed
 * to: the mark lives for three seconds, so a preference changed mid-flash has nothing to correct.
 * With less motion the tint is drawn still, and taken away when its time is up.
 */
export function prefersReducedMotion(): boolean {
  const preference = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)");
  return preference !== undefined && preference.matches;
}
